import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ListButton } from '@app/core/components/list/list.component';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ProductSelectionItem, ProductSelectionParams, ProductService, SelectProductGet } from '@app/services/product.service';

@Component({
    selector: 'tt-select-product',
    templateUrl: './select-product.component.html',
    styleUrls: ['./select-product.component.css'],
    standalone: false
})
export class SelectProductComponent implements OnInit {
    public page?: SelectProductGet;

    public params: ProductSelectionParams = {
        webpage_name: '',
        parm1: '',
        parm2: ''
    };

    public groups: Item[] = [];

    public products: ProductSelectionItem[] = [];

    public componentProducts: ProductSelectionItem[] = [];

    public selectSearch: 'SELECT' | 'SEARCH' = 'SELECT';

    public searchSelect: 'SELECT' | 'SEARCH' = 'SEARCH';

    public project: 'PJ' | 'JOB' = 'PJ';

    public loading = false;

    public selecting = false;

    public error = '';

    private pendingLoads = 0;

    public canSelect = () => !this.selecting;

    constructor(
        private product: ProductService,
        private remember: RememberService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public get mode(): 'SELECT' | 'SEARCH' {
        return this.params.webpage_name === 'materialregister' ? this.selectSearch : this.searchSelect;
    }

    public async ngOnInit(): Promise<void> {
        const params = this.navigator?.params;

        this.params = {
            webpage_name: String(params?.['webpage_name'] ?? ''),
            parm1: String(params?.['parm1'] ?? ''),
            parm2: String(params?.['parm2'] ?? '')
        };

        await Promise.all([this.loadPage(), this.loadGroups(), this.loadComponentProducts()]);
    }

    private async loadPage(): Promise<void> {
        try {
            this.page = await this.product.getSelectionSettings(this.params);

            if (this.page.prodgrp_id !== 'ALL' || this.page.searchtext !== '') await this.loadProducts();
        } catch (error) { this.showError(error); }
    }

    private async loadGroups(): Promise<void> {
        try { this.groups = await this.product.getProductGroups({ add_all: '1' }); }
        catch (error) { this.showError(error); }
    }

    public async loadProducts(): Promise<void> {
        const page = this.page;

        if (!page || typeof page.prodgrp_id !== 'string' || page.prodgrp_id.trim() === '' || typeof page.searchtext !== 'string') return;

        this.products = [];
        this.loading = true;
        ++this.pendingLoads;

        try {
            this.products = (await this.product.getProducts({ prodgrp_id: page.prodgrp_id, prod_name: page.searchtext, ...this.params })).map(row => ({ ...row }));

            if (this.params.webpage_name === 'custactcomp' && this.products.length === 1) {
                // Legacy automatic acceptance starts remembering and returns without waiting.
                void this.rememberProduct(this.products[0]).catch(error => this.showError(error));
                this.navigator?.back();
            }
        } catch (error) { this.showError(error); }
        finally { this.loading = --this.pendingLoads > 0; }
    }

    public async loadComponentProducts(): Promise<void> {
        this.componentProducts = [];

        try {
            this.componentProducts = await this.product.listComponentProducts({
                custact_no: this.params.parm1,
                select_status2: this.project,
                include_top: '1',
                retrievetype: 'child'
            });
        } catch (error) { this.showError(error); }
    }

    public changeMode(value: string): void {
        if (value !== 'SELECT' && value !== 'SEARCH') return;

        if (this.params.webpage_name === 'materialregister') this.selectSearch = value;
        else this.searchSelect = value;

        if (value === 'SELECT') void this.loadComponentProducts();
    }

    public changeProject(value: string): void {
        if (value !== 'PJ' && value !== 'JOB') return;

        this.project = value;

        if (this.selectSearch === 'SELECT' || this.searchSelect === 'SELECT') void this.loadComponentProducts();
    }

    public changeGroup(value: string): void {
        if (!this.page || value === undefined || value === this.page.prodgrp_id) return;

        this.page.prodgrp_id = value;

        if (typeof value !== 'string' || value.trim() === '') return;

        void this.loadProducts();
    }

    public changeSearch(value: string): void {
        if (!this.page || value === undefined || value === this.page.searchtext) return;

        this.page.searchtext = value;

        if (value === '') this.clearSearch();
    }

    public clearSearch(): void {
        if (!this.page) return;

        this.page.searchtext = '';

        this.rememberField('searchtext');

        if (this.page.prodgrp_id !== 'ALL') void this.loadProducts();
        else this.products = [];
    }

    public search(): void {
        if (!this.page || this.loading || !(this.page.searchtext > '')) return;

        this.rememberField('searchtext');
        void this.loadProducts();
    }

    private rememberField(field: 'searchtext'): void {
        if (!this.page) return;

        const name = `w_selectproduct.${this.params.webpage_name}/${this.params.parm1}.${field}`;
        void this.remember.remember(name, this.page[field]).catch(error => this.showError(error));
    }

    private async rememberProduct(item: ProductSelectionItem): Promise<void> {
        if (['materialregister', 'orderlineedit', 'custactcomp', 'equipmentedit'].includes(this.params.webpage_name) && typeof item.item_id === 'string' && item.item_id.trim() !== '') {
            await this.remember.remember('w_selectproduct.prod_id', item.item_id);
        }
    }

    public async select(item: ProductSelectionItem): Promise<void> {
        if (item.item_id === '' || this.selecting) return;

        this.selecting = true;

        try { await this.rememberProduct(item); this.navigator?.back(); }
        catch (error) { this.showError(error); }
        finally { this.selecting = false; }
    }

    public get visibleProducts(): ProductSelectionItem[] {
        if (this.mode === 'SEARCH') return this.products;

        const search = this.page?.filterval ?? '';

        if (search === '') return this.componentProducts;

        const words = search.toLowerCase().split(' ');

        return this.componentProducts.filter((item, index, items) => items.indexOf(item) === index && (item.item_id === '0' || item.item_id === '-1' || item.item_filtervalue === undefined || words.every(word => item.item_filtervalue!.toLowerCase().includes(word))));
    }

    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
